import { createContext } from "react";
import type { User, Session } from "@supabase/supabase-js";

/**
 * User profile data structure
 *
 * Maps to the `profiles` table in Supabase
 * Automatically created when a user signs up via database trigger
 */
export interface Profile {
  id: string;
  email: string;
  created_at: string;
  updated_at: string;
  privacy_consent_accepted?: boolean | null;
  privacy_consent_date?: string | null;
  privacy_consent_version?: string | null;
  terms_consent_accepted?: boolean | null;
  terms_consent_date?: string | null;
  terms_consent_version?: string | null;
  client_encryption_enabled?: boolean | null;
  client_encryption_enabled_at?: string | null;
  client_encryption_salt?: string | null;
  client_encryption_verify_data?: string | null;
  client_encryption_auto_lock_enabled?: boolean | null;
  client_encryption_auto_lock_timeout?: number | null;
  // Fields for safe encryption key change
  client_encryption_old_salt?: string | null;
  client_encryption_old_verify_data?: string | null;
  client_encryption_migration_in_progress?: boolean | null;
}

/**
 * Authentication context value type
 *
 * Provides authentication state and methods throughout the application
 * Must be consumed via the useAuth() hook within an AuthProvider
 */
export interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, captchaToken?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: any }>;

  // 2FA Methods
  enrollMFA: () => Promise<{
    error: any;
    qrCodeUri?: string;
    secret?: string;
    factorId?: string;
  }>;
  verifyMFAEnrollment: (factorId: string, code: string) => Promise<{ error: any }>;
  challengeMFA: (factorId: string) => Promise<{
    error: any;
    challengeId?: string;
  }>;
  verifyMFAChallenge: (
    factorId: string,
    challengeId: string,
    code: string
  ) => Promise<{ error: any }>;
  unenrollMFA: (factorId: string) => Promise<{ error: any }>;
  getMFAFactors: () => Promise<{
    error: any;
    factors?: any[];
  }>;

  // AAL (Authenticator Assurance Level) for secure MFA verification
  currentAAL: "aal1" | "aal2" | null;
  nextAAL: "aal1" | "aal2" | null;
  mfaVerified: boolean; // true if AAL2 reached or no MFA required
  hasMFAEnabled: boolean; // true if verified TOTP factor exists
  checkAALStatus: () => Promise<{ mfaVerified: boolean; hasMFAEnabled: boolean }>;

  // Computed state: User is fully authenticated only when required auth level is met
  // - If no MFA: user exists = fully authenticated
  // - If MFA enabled: user exists AND AAL2 reached = fully authenticated
  isFullyAuthenticated: boolean;

  // Loading state for AAL check - true until initial AAL verification is complete
  aalCheckLoading: boolean;

  // Trusted device state - true when device was recognized as trusted during login
  // Allows MFA skip for normal users (not admin/tester)
  deviceTrusted: boolean;
  setDeviceTrusted: (trusted: boolean) => void;
}

/**
 * Authentication Context (Definition Only)
 *
 * This file contains ONLY the context definition and types.
 * The actual provider implementation is in AuthContext.tsx (PascalCase).
 *
 * **Architecture Pattern**: Separation of Concerns
 * - This file: Pure definitions (context object + types)
 * - AuthContext.tsx: Implementation (provider component + business logic)
 *
 * **Why separate files?**
 * 1. React Fast Refresh compatibility (avoids warnings when mixing contexts + components)
 * 2. Better separation of concerns (definition vs. implementation)
 * 3. Easier testing (can mock context without loading provider logic)
 * 4. Prevents circular dependencies
 *
 * **Usage**:
 * - Import this file in hooks: `import { AuthContext } from "@/contexts/auth-context"`
 * - Import AuthContext.tsx for provider: `import { AuthProvider } from "@/contexts/AuthContext"`
 *
 * @see AuthContext.tsx for the provider implementation
 * @see useAuth.ts for the consumer hook
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);
